// AudreyDashboard - Cockpit de gouvernance Audrey ÉDITABLE dans OmniHub
// Dashboard centralisé : État, Scripts, Règles, Intents - FULL EDIT MODE
import { useState, useEffect, useCallback } from "react";
import {
  Phone,
  PhoneIncoming,
  Mic,
  Settings,
  BarChart3,
  Clock,
  CheckCircle,
  XCircle,
  AlertTriangle,
  Loader2,
  RefreshCw,
  Play,
  Edit,
  Save,
  X,
  Volume2,
  FileText,
  Activity,
  MessageSquare,
  Zap,
  Shield,
  Brain,
  ChevronRight,
  ChevronDown,
  Eye,
  History,
  Target,
  Plus,
  Trash2,
  GripVertical,
  Copy,
  ToggleLeft,
  ToggleRight,
  ArrowUp,
  ArrowDown,
  AlertCircle,
  Info,
  Sparkles
} from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { Badge } from "../../components/ui/badge";
import { Input } from "../../components/ui/input";
import { Textarea } from "../../components/ui/textarea";
import { ScrollArea } from "../../components/ui/scroll-area";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../../components/ui/tabs";
import { Switch } from "../../components/ui/switch";
import { Label } from "../../components/ui/label";
import { Slider } from "../../components/ui/slider";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../../components/ui/dialog";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "../../components/ui/accordion";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../../components/ui/table";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "../../components/ui/tooltip";
import { toast } from "sonner";
import axios from "axios";

const API = process.env.REACT_APP_BACKEND_URL;

// Statut badge styles
const statusStyles = {
  active: "bg-green-500/20 text-green-400 border-green-500/30",
  inactive: "bg-zinc-500/20 text-zinc-400 border-zinc-500/30",
  error: "bg-red-500/20 text-red-400 border-red-500/30",
  warning: "bg-yellow-500/20 text-yellow-400 border-yellow-500/30"
};

// Priority colors
const priorityColors = {
  1: "bg-red-500/20 text-red-400 border-red-500/30",
  2: "bg-orange-500/20 text-orange-400 border-orange-500/30",
  3: "bg-yellow-500/20 text-yellow-400 border-yellow-500/30",
  4: "bg-blue-500/20 text-blue-400 border-blue-500/30",
  5: "bg-green-500/20 text-green-400 border-green-500/30",
  6: "bg-zinc-500/20 text-zinc-400 border-zinc-500/30",
  99: "bg-zinc-700/20 text-zinc-500 border-zinc-600/30"
};

export default function AudreyDashboard() {
  const [activeTab, setActiveTab] = useState("overview");
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  
  // Data states
  const [runtimeStatus, setRuntimeStatus] = useState(null);
  const [config, setConfig] = useState(null);
  const [originalConfig, setOriginalConfig] = useState(null);
  const [metrics, setMetrics] = useState(null);
  
  // Edit tracking
  const [hasChanges, setHasChanges] = useState(false);
  const [editingIntent, setEditingIntent] = useState(null);
  const [editingScript, setEditingScript] = useState(null);
  const [editingRule, setEditingRule] = useState(null);
  
  // Dialog states
  const [showTestDialog, setShowTestDialog] = useState(false);
  const [testInput, setTestInput] = useState("");
  const [testResult, setTestResult] = useState(null);
  const [showSaveConfirm, setShowSaveConfirm] = useState(false);

  // Load all data
  const loadData = useCallback(async () => {
    try {
      setRefreshing(true);
      
      const [runtimeRes, configRes, callsRes] = await Promise.all([
        axios.get(`${API}/api/audrey/runtime/status`).catch(() => ({ data: null })),
        axios.get(`${API}/api/audrey/config-raw`).catch(() => ({ data: null })),
        axios.get(`${API}/api/calls?limit=20`).catch(() => ({ data: [] }))
      ]);
      
      setRuntimeStatus(runtimeRes.data);
      
      // Parse config
      if (configRes.data?.content) {
        try {
          const parsedConfig = JSON.parse(configRes.data.content);
          setConfig(parsedConfig);
          setOriginalConfig(JSON.parse(configRes.data.content)); // Deep copy
        } catch (e) {
          console.error("Error parsing config:", e);
        }
      }
      
      // Calculate metrics from calls
      const calls = callsRes.data || [];
      if (calls.length > 0) {
        const today = new Date().toISOString().split('T')[0];
        const todayCalls = calls.filter(c => c.timestamp?.startsWith(today) || c.created_at?.startsWith(today));
        
        setMetrics({
          totalCalls: calls.length,
          todayCalls: todayCalls.length,
          avgDuration: calls.reduce((acc, c) => acc + (c.duration || 0), 0) / calls.length || 0,
          qualifiedRate: (calls.filter(c => c.intent_detected && c.intent_detected !== 'non_classifie').length / calls.length * 100) || 0
        });
      }
      
    } catch (error) {
      console.error("Error loading Audrey data:", error);
      toast.error("Erreur lors du chargement des données");
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, []);

  useEffect(() => {
    loadData();
  }, [loadData]);

  // Track changes
  useEffect(() => {
    if (config && originalConfig) {
      const changed = JSON.stringify(config) !== JSON.stringify(originalConfig);
      setHasChanges(changed);
    }
  }, [config, originalConfig]);

  // Update config helper
  const updateConfig = (path, value) => {
    setConfig(prev => {
      const newConfig = JSON.parse(JSON.stringify(prev));
      const keys = path.split('.');
      let current = newConfig;
      for (let i = 0; i < keys.length - 1; i++) {
        if (keys[i].includes('[')) {
          const [key, idx] = keys[i].replace(']', '').split('[');
          current = current[key][parseInt(idx)];
        } else {
          current = current[keys[i]];
        }
      }
      const lastKey = keys[keys.length - 1];
      if (lastKey.includes('[')) {
        const [key, idx] = lastKey.replace(']', '').split('[');
        current[key][parseInt(idx)] = value;
      } else {
        current[lastKey] = value;
      }
      return newConfig;
    });
  };

  // Save configuration
  const saveConfig = async () => {
    if (!config) return;
    
    setSaving(true);
    try {
      // Update last_updated
      const updatedConfig = {
        ...config,
        last_updated: new Date().toISOString(),
        updated_by: "omnihub_ui"
      };
      
      const response = await axios.post(`${API}/api/audrey/config`, updatedConfig);
      
      if (response.data.success) {
        toast.success("Configuration sauvegardée avec succès");
        setOriginalConfig(JSON.parse(JSON.stringify(updatedConfig)));
        setConfig(updatedConfig);
        setHasChanges(false);
        setShowSaveConfirm(false);
      } else {
        toast.error("Erreur lors de la sauvegarde");
      }
    } catch (error) {
      console.error("Save error:", error);
      toast.error(error.response?.data?.detail || "Erreur lors de la sauvegarde");
    } finally {
      setSaving(false);
    }
  };

  // Discard changes
  const discardChanges = () => {
    if (originalConfig) {
      setConfig(JSON.parse(JSON.stringify(originalConfig)));
      setHasChanges(false);
      toast.info("Modifications annulées");
    }
  };

  // Test classification
  const testClassification = async () => {
    if (!testInput.trim()) return;
    
    try {
      const response = await axios.post(`${API}/api/audrey/test-classification`, {
        text: testInput
      });
      setTestResult(response.data);
    } catch (error) {
      toast.error("Erreur lors du test");
    }
  };

  // Update intent
  const updateIntent = (intentId, field, value) => {
    setConfig(prev => {
      const newConfig = JSON.parse(JSON.stringify(prev));
      const intentIdx = newConfig.intents.findIndex(i => i.id === intentId);
      if (intentIdx !== -1) {
        newConfig.intents[intentIdx][field] = value;
      }
      return newConfig;
    });
  };

  // Update script
  const updateScript = (intentId, field, value) => {
    setConfig(prev => {
      const newConfig = JSON.parse(JSON.stringify(prev));
      const scriptIdx = newConfig.scripts.findIndex(s => s.intent_id === intentId);
      if (scriptIdx !== -1) {
        newConfig.scripts[scriptIdx][field] = value;
      }
      return newConfig;
    });
  };

  // Add keyword to intent
  const addKeyword = (intentId, keyword) => {
    if (!keyword.trim()) return;
    setConfig(prev => {
      const newConfig = JSON.parse(JSON.stringify(prev));
      const intentIdx = newConfig.intents.findIndex(i => i.id === intentId);
      if (intentIdx !== -1) {
        if (!newConfig.intents[intentIdx].keywords) {
          newConfig.intents[intentIdx].keywords = [];
        }
        if (!newConfig.intents[intentIdx].keywords.includes(keyword.trim())) {
          newConfig.intents[intentIdx].keywords.push(keyword.trim());
        }
      }
      return newConfig;
    });
  };

  // Remove keyword from intent
  const removeKeyword = (intentId, keyword) => {
    setConfig(prev => {
      const newConfig = JSON.parse(JSON.stringify(prev));
      const intentIdx = newConfig.intents.findIndex(i => i.id === intentId);
      if (intentIdx !== -1 && newConfig.intents[intentIdx].keywords) {
        newConfig.intents[intentIdx].keywords = newConfig.intents[intentIdx].keywords.filter(k => k !== keyword);
      }
      return newConfig;
    });
  };

  // Update escalation rule
  const updateEscalationRule = (ruleId, field, value) => {
    setConfig(prev => {
      const newConfig = JSON.parse(JSON.stringify(prev));
      const ruleIdx = newConfig.escalation_rules.findIndex(r => r.id === ruleId);
      if (ruleIdx !== -1) {
        newConfig.escalation_rules[ruleIdx][field] = value;
      }
      return newConfig;
    });
  };

  // Render loading
  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <Loader2 className="w-8 h-8 animate-spin text-purple-400" />
        <span className="ml-3 text-zinc-400">Chargement du cockpit Audrey...</span>
      </div>
    );
  }

  return (
    <TooltipProvider>
      <div className="space-y-6" data-testid="audrey-dashboard">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-white flex items-center gap-3">
              <div className="p-2 bg-purple-500/20 rounded-lg">
                <Phone className="w-6 h-6 text-purple-400" />
              </div>
              Cockpit Audrey
              <span className="text-zinc-500 font-normal">—</span>
              <span className="text-lg font-normal text-purple-400">Gouvernance Agent Vocal</span>
            </h1>
            <p className="text-zinc-400 mt-1 ml-14">
              Configuration complète • Édition en temps réel • Source de vérité
            </p>
          </div>
          
          <div className="flex items-center gap-3">
            {/* Status indicator */}
            <Badge className={runtimeStatus?.runtime_initialized ? statusStyles.active : statusStyles.error}>
              {runtimeStatus?.runtime_initialized ? (
                <><CheckCircle className="w-3 h-3 mr-1" /> Runtime actif</>
              ) : (
                <><XCircle className="w-3 h-3 mr-1" /> Runtime inactif</>
              )}
            </Badge>
            
            {hasChanges && (
              <Badge className={statusStyles.warning}>
                <AlertTriangle className="w-3 h-3 mr-1" /> Modifications non sauvegardées
              </Badge>
            )}
            
            <Button
              variant="outline"
              size="sm"
              onClick={loadData}
              disabled={refreshing}
              className="border-zinc-700"
            >
              <RefreshCw className={`w-4 h-4 mr-2 ${refreshing ? 'animate-spin' : ''}`} />
              Actualiser
            </Button>

            {hasChanges && (
              <>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={discardChanges}
                  className="border-zinc-700 text-zinc-400"
                >
                  <X className="w-4 h-4 mr-2" />
                  Annuler
                </Button>
                <Button
                  size="sm"
                  onClick={() => setShowSaveConfirm(true)}
                  className="bg-green-600 hover:bg-green-700"
                  disabled={saving}
                >
                  {saving ? (
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  ) : (
                    <Save className="w-4 h-4 mr-2" />
                  )}
                  Sauvegarder
                </Button>
              </>
            )}
          </div>
        </div>

        {/* Main Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-6">
          <TabsList className="bg-zinc-900 border border-zinc-800">
            <TabsTrigger value="overview" className="data-[state=active]:bg-purple-500/20">
              <Activity className="w-4 h-4 mr-2" />
              Vue d'ensemble
            </TabsTrigger>
            <TabsTrigger value="identity" className="data-[state=active]:bg-purple-500/20">
              <Settings className="w-4 h-4 mr-2" />
              Identité
            </TabsTrigger>
            <TabsTrigger value="intents" className="data-[state=active]:bg-purple-500/20">
              <Brain className="w-4 h-4 mr-2" />
              Intents & Priorités
            </TabsTrigger>
            <TabsTrigger value="scripts" className="data-[state=active]:bg-purple-500/20">
              <FileText className="w-4 h-4 mr-2" />
              Scripts & Textes
            </TabsTrigger>
            <TabsTrigger value="rules" className="data-[state=active]:bg-purple-500/20">
              <Shield className="w-4 h-4 mr-2" />
              Règles
            </TabsTrigger>
            <TabsTrigger value="test" className="data-[state=active]:bg-purple-500/20">
              <Sparkles className="w-4 h-4 mr-2" />
              Test
            </TabsTrigger>
          </TabsList>

          {/* TAB: Overview */}
          <TabsContent value="overview" className="space-y-6">
            {/* KPIs */}
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              <Card className="bg-zinc-900 border-zinc-800">
                <CardContent className="pt-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-xs text-zinc-500 uppercase tracking-wider">Sessions actives</p>
                      <p className="text-2xl font-bold text-white mt-1">
                        {runtimeStatus?.active_sessions_count || 0}
                      </p>
                    </div>
                    <div className="p-3 bg-green-500/10 rounded-lg">
                      <PhoneIncoming className="w-6 h-6 text-green-400" />
                    </div>
                  </div>
                </CardContent>
              </Card>

              <Card className="bg-zinc-900 border-zinc-800">
                <CardContent className="pt-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-xs text-zinc-500 uppercase tracking-wider">Appels aujourd'hui</p>
                      <p className="text-2xl font-bold text-white mt-1">
                        {metrics?.todayCalls || 0}
                      </p>
                    </div>
                    <div className="p-3 bg-blue-500/10 rounded-lg">
                      <Phone className="w-6 h-6 text-blue-400" />
                    </div>
                  </div>
                </CardContent>
              </Card>

              <Card className="bg-zinc-900 border-zinc-800">
                <CardContent className="pt-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-xs text-zinc-500 uppercase tracking-wider">Intents actifs</p>
                      <p className="text-2xl font-bold text-white mt-1">
                        {config?.intents?.filter(i => i.enabled).length || 0} / {config?.intents?.length || 0}
                      </p>
                    </div>
                    <div className="p-3 bg-purple-500/10 rounded-lg">
                      <Brain className="w-6 h-6 text-purple-400" />
                    </div>
                  </div>
                </CardContent>
              </Card>

              <Card className="bg-zinc-900 border-zinc-800">
                <CardContent className="pt-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-xs text-zinc-500 uppercase tracking-wider">Taux qualification</p>
                      <p className="text-2xl font-bold text-white mt-1">
                        {Math.round(metrics?.qualifiedRate || 0)}%
                      </p>
                    </div>
                    <div className="p-3 bg-emerald-500/10 rounded-lg">
                      <Target className="w-6 h-6 text-emerald-400" />
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* Configuration Summary */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* Agent Info */}
              <Card className="bg-zinc-900 border-zinc-800">
                <CardHeader>
                  <CardTitle className="text-lg flex items-center gap-2">
                    <Mic className="w-5 h-5 text-purple-400" />
                    Agent Actif
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  {config?.identity && (
                    <>
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                        <div className="p-3 bg-zinc-800/50 rounded-lg">
                          <p className="text-xs text-zinc-500">Nom</p>
                          <p className="font-medium text-white">{config.identity.name}</p>
                        </div>
                        <div className="p-3 bg-zinc-800/50 rounded-lg">
                          <p className="text-xs text-zinc-500">Version</p>
                          <p className="font-medium text-white">{config.identity.version}</p>
                        </div>
                        <div className="p-3 bg-zinc-800/50 rounded-lg col-span-2">
                          <p className="text-xs text-zinc-500">Rôle</p>
                          <p className="font-medium text-white text-sm">{config.identity.rôle}</p>
                        </div>
                      </div>

                      <div className="p-3 bg-zinc-800/50 rounded-lg">
                        <p className="text-xs text-zinc-500 mb-2">Permissions actives</p>
                        <div className="flex flex-wrap gap-2">
                          {config.identity.permissions?.can_send_sms && (
                            <Badge variant="outline" className="border-green-500/30 text-green-400">SMS</Badge>
                          )}
                          {config.identity.permissions?.can_transfer_call && (
                            <Badge variant="outline" className="border-blue-500/30 text-blue-400">Transfert</Badge>
                          )}
                          {config.identity.permissions?.can_collect_data && (
                            <Badge variant="outline" className="border-purple-500/30 text-purple-400">Collecte</Badge>
                          )}
                          {config.identity.permissions?.can_end_call && (
                            <Badge variant="outline" className="border-orange-500/30 text-orange-400">Raccrochage</Badge>
                          )}
                        </div>
                      </div>
                    </>
                  )}
                </CardContent>
              </Card>

              {/* Quick Stats */}
              <Card className="bg-zinc-900 border-zinc-800">
                <CardHeader>
                  <CardTitle className="text-lg flex items-center gap-2">
                    <BarChart3 className="w-5 h-5 text-cyan-400" />
                    Résumé Configuration
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-3">
                    <div className="flex items-center justify-between p-3 bg-zinc-800/50 rounded-lg">
                      <span className="text-zinc-400">Intents configurés</span>
                      <Badge variant="outline">{config?.intents?.length || 0}</Badge>
                    </div>
                    <div className="flex items-center justify-between p-3 bg-zinc-800/50 rounded-lg">
                      <span className="text-zinc-400">Scripts définis</span>
                      <Badge variant="outline">{config?.scripts?.length || 0}</Badge>
                    </div>
                    <div className="flex items-center justify-between p-3 bg-zinc-800/50 rounded-lg">
                      <span className="text-zinc-400">Règles d'escalade</span>
                      <Badge variant="outline">{config?.escalation_rules?.length || 0}</Badge>
                    </div>
                    <div className="flex items-center justify-between p-3 bg-zinc-800/50 rounded-lg">
                      <span className="text-zinc-400">Actions définies</span>
                      <Badge variant="outline">{config?.actions?.length || 0}</Badge>
                    </div>
                    <div className="flex items-center justify-between p-3 bg-zinc-800/50 rounded-lg">
                      <span className="text-zinc-400">Dernière mise à jour</span>
                      <span className="text-xs text-zinc-500">
                        {config?.last_updated ? new Date(config.last_updated).toLocaleString('fr-FR') : 'N/A'}
                      </span>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>
          </TabsContent>

          {/* TAB: Identity */}
          <TabsContent value="identity" className="space-y-6">
            <Card className="bg-zinc-900 border-zinc-800">
              <CardHeader>
                <CardTitle className="text-lg flex items-center gap-2">
                  <Settings className="w-5 h-5 text-purple-400" />
                  Identité de l'Agent
                </CardTitle>
                <CardDescription>
                  Configurez les informations de base et les permissions d'Audrey
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                {config?.identity && (
                  <>
                    {/* Basic Info */}
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label>Nom de l'agent</Label>
                        <Input
                          value={config.identity.name || ''}
                          onChange={(e) => updateConfig('identity.name', e.target.value)}
                          className="bg-zinc-800 border-zinc-700"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label>Version</Label>
                        <Input
                          value={config.identity.version || ''}
                          onChange={(e) => updateConfig('identity.version', e.target.value)}
                          className="bg-zinc-800 border-zinc-700"
                        />
                      </div>
                    </div>

                    <div className="space-y-2">
                      <Label>Nom d'affichage</Label>
                      <Input
                        value={config.identity.display_name || ''}
                        onChange={(e) => updateConfig('identity.display_name', e.target.value)}
                        className="bg-zinc-800 border-zinc-700"
                      />
                    </div>

                    <div className="space-y-2">
                      <Label>Rôle</Label>
                      <Input
                        value={config.identity.rôle || ''}
                        onChange={(e) => updateConfig('identity.rôle', e.target.value)}
                        className="bg-zinc-800 border-zinc-700"
                      />
                    </div>

                    <div className="space-y-2">
                      <Label>Description</Label>
                      <Textarea
                        value={config.identity.description || ''}
                        onChange={(e) => updateConfig('identity.description', e.target.value)}
                        className="bg-zinc-800 border-zinc-700 min-h-[100px]"
                      />
                    </div>

                    {/* Statut */}
                    <div className="flex items-center justify-between p-4 bg-zinc-800 rounded-lg">
                      <div>
                        <Label className="text-white">Statut de l'agent</Label>
                        <p className="text-xs text-zinc-500">Activer ou désactiver l'agent vocal</p>
                      </div>
                      <div className="flex items-center gap-2">
                        <Badge className={config.identity.status === 'active' ? statusStyles.active : statusStyles.inactive}>
                          {config.identity.status === 'active' ? 'Actif' : 'Inactif'}
                        </Badge>
                        <Switch
                          checked={config.identity.status === 'active'}
                          onCheckedChange={(checked) => updateConfig('identity.status', checked ? 'active' : 'inactive')}
                        />
                      </div>
                    </div>

                    {/* Permissions */}
                    <div className="space-y-4">
                      <Label className="text-white text-base">Permissions</Label>
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                        {[
                          { key: 'can_send_sms', label: 'Envoi SMS', desc: 'Peut envoyer des SMS de confirmation' },
                          { key: 'can_send_email', label: 'Envoi Email', desc: 'Peut envoyer des emails' },
                          { key: 'can_transfer_call', label: 'Transfert appel', desc: 'Peut transférer vers un humain' },
                          { key: 'can_end_call', label: 'Fin d\'appel', desc: 'Peut raccrocher automatiquement' },
                          { key: 'can_collect_data', label: 'Collecte données', desc: 'Peut collecter des informations' }
                        ].map((perm) => (
                          <div key={perm.key} className="flex items-center justify-between p-3 bg-zinc-800 rounded-lg">
                            <div>
                              <p className="text-sm text-white">{perm.label}</p>
                              <p className="text-xs text-zinc-500">{perm.desc}</p>
                            </div>
                            <Switch
                              checked={config.identity.permissions?.[perm.key] || false}
                              onCheckedChange={(checked) => {
                                setConfig(prev => ({
                                  ...prev,
                                  identity: {
                                    ...prev.identity,
                                    permissions: {
                                      ...prev.identity.permissions,
                                      [perm.key]: checked
                                    }
                                  }
                                }));
                              }}
                            />
                          </div>
                        ))}
                      </div>
                    </div>

                    {/* Horaires */}
                    <div className="space-y-4">
                      <Label className="text-white text-base">Horaires d'activité</Label>
                      <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4">
                        <div className="space-y-2">
                          <Label className="text-xs text-zinc-500">Début</Label>
                          <Input
                            type="time"
                            value={config.identity.active_hours?.start || '00:00'}
                            onChange={(e) => {
                              setConfig(prev => ({
                                ...prev,
                                identity: {
                                  ...prev.identity,
                                  active_hours: {
                                    ...prev.identity.active_hours,
                                    start: e.target.value
                                  }
                                }
                              }));
                            }}
                            className="bg-zinc-800 border-zinc-700"
                          />
                        </div>
                        <div className="space-y-2">
                          <Label className="text-xs text-zinc-500">Fin</Label>
                          <Input
                            type="time"
                            value={config.identity.active_hours?.end || '23:59'}
                            onChange={(e) => {
                              setConfig(prev => ({
                                ...prev,
                                identity: {
                                  ...prev.identity,
                                  active_hours: {
                                    ...prev.identity.active_hours,
                                    end: e.target.value
                                  }
                                }
                              }));
                            }}
                            className="bg-zinc-800 border-zinc-700"
                          />
                        </div>
                        <div className="space-y-2">
                          <Label className="text-xs text-zinc-500">Fuseau horaire</Label>
                          <Select
                            value={config.identity.active_hours?.timezone || 'Europe/Paris'}
                            onValueChange={(value) => {
                              setConfig(prev => ({
                                ...prev,
                                identity: {
                                  ...prev.identity,
                                  active_hours: {
                                    ...prev.identity.active_hours,
                                    timezone: value
                                  }
                                }
                              }));
                            }}
                          >
                            <SelectTrigger className="bg-zinc-800 border-zinc-700">
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="Europe/Paris">Europe/Paris</SelectItem>
                              <SelectItem value="UTC">UTC</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>
                      </div>
                    </div>
                  </>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* TAB: Intents */}
          <TabsContent value="intents" className="space-y-6">
            <Card className="bg-zinc-900 border-zinc-800">
              <CardHeader>
                <CardTitle className="text-lg flex items-center gap-2">
                  <Brain className="w-5 h-5 text-purple-400" />
                  Intents & Classification
                </CardTitle>
                <CardDescription>
                  Gérez les intentions détectées, leurs priorités et mots-clés déclencheurs
                </CardDescription>
              </CardHeader>
              <CardContent>
                <ScrollArea className="h-[600px] pr-4">
                  <div className="space-y-4">
                    {config?.intents?.sort((a, b) => (a.priority || 99) - (b.priority || 99)).map((intent) => (
                      <div 
                        key={intent.id}
                        className={`p-4 rounded-lg border ${
                          intent.enabled ? 'border-green-500/30 bg-zinc-800/50' : 'border-zinc-700 bg-zinc-800/30'
                        }`}
                      >
                        {/* Intent Header */}
                        <div className="flex items-center justify-between mb-4">
                          <div className="flex items-center gap-3">
                            <Badge className={priorityColors[intent.priority] || priorityColors[99]}>
                              P{intent.priority || 99}
                            </Badge>
                            <div>
                              <p className="font-medium text-white">{intent.display_name || intent.name}</p>
                              <p className="text-xs text-zinc-500 font-mono">{intent.id}</p>
                            </div>
                            {intent.is_fallback && (
                              <Badge className="bg-yellow-500/20 text-yellow-400">Fallback</Badge>
                            )}
                          </div>
                          <div className="flex items-center gap-2">
                            <Switch
                              checked={intent.enabled}
                              onCheckedChange={(checked) => updateIntent(intent.id, 'enabled', checked)}
                            />
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => setEditingIntent(editingIntent === intent.id ? null : intent.id)}
                            >
                              {editingIntent === intent.id ? (
                                <ChevronDown className="w-4 h-4" />
                              ) : (
                                <Edit className="w-4 h-4" />
                              )}
                            </Button>
                          </div>
                        </div>

                        {/* Description */}
                        <p className="text-sm text-zinc-400 mb-3">{intent.description}</p>

                        {/* Expanded Edit Section */}
                        {editingIntent === intent.id && (
                          <div className="space-y-4 pt-4 border-t border-zinc-700">
                            {/* Priority & Threshold */}
                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                              <div className="space-y-2">
                                <Label className="text-xs text-zinc-500">Priorité (1=haute, 99=basse)</Label>
                                <Input
                                  type="number"
                                  min="1"
                                  max="99"
                                  value={intent.priority || 99}
                                  onChange={(e) => updateIntent(intent.id, 'priority', parseInt(e.target.value))}
                                  className="bg-zinc-900 border-zinc-700"
                                />
                              </div>
                              <div className="space-y-2">
                                <Label className="text-xs text-zinc-500">Seuil de confiance ({Math.round((intent.confidence_threshold || 0) * 100)}%)</Label>
                                <Slider
                                  value={[(intent.confidence_threshold || 0) * 100]}
                                  onValueChange={([value]) => updateIntent(intent.id, 'confidence_threshold', value / 100)}
                                  max={100}
                                  step={5}
                                  className="py-2"
                                />
                              </div>
                            </div>

                            {/* Display Name */}
                            <div className="space-y-2">
                              <Label className="text-xs text-zinc-500">Nom d'affichage</Label>
                              <Input
                                value={intent.display_name || ''}
                                onChange={(e) => updateIntent(intent.id, 'display_name', e.target.value)}
                                className="bg-zinc-900 border-zinc-700"
                              />
                            </div>

                            {/* Description */}
                            <div className="space-y-2">
                              <Label className="text-xs text-zinc-500">Description</Label>
                              <Textarea
                                value={intent.description || ''}
                                onChange={(e) => updateIntent(intent.id, 'description', e.target.value)}
                                className="bg-zinc-900 border-zinc-700"
                              />
                            </div>

                            {/* Keywords */}
                            <div className="space-y-2">
                              <Label className="text-xs text-zinc-500">Mots-clés déclencheurs ({intent.keywords?.length || 0})</Label>
                              <div className="flex flex-wrap gap-2 mb-2">
                                {intent.keywords?.map((kw, idx) => (
                                  <Badge 
                                    key={idx} 
                                    variant="outline" 
                                    className="border-zinc-600 cursor-pointer hover:border-red-500 hover:text-red-400"
                                    onClick={() => removeKeyword(intent.id, kw)}
                                  >
                                    {kw} <X className="w-3 h-3 ml-1" />
                                  </Badge>
                                ))}
                              </div>
                              <div className="flex gap-2">
                                <Input
                                  placeholder="Ajouter un mot-clé..."
                                  className="bg-zinc-900 border-zinc-700"
                                  onKeyPress={(e) => {
                                    if (e.key === 'Enter') {
                                      addKeyword(intent.id, e.target.value);
                                      e.target.value = '';
                                    }
                                  }}
                                />
                                <Button
                                  variant="outline"
                                  size="sm"
                                  onClick={(e) => {
                                    const input = e.target.previousSibling;
                                    if (input?.value) {
                                      addKeyword(intent.id, input.value);
                                      input.value = '';
                                    }
                                  }}
                                >
                                  <Plus className="w-4 h-4" />
                                </Button>
                              </div>
                            </div>

                            {/* Examples */}
                            {intent.examples?.length > 0 && (
                              <div className="space-y-2">
                                <Label className="text-xs text-zinc-500">Exemples de phrases</Label>
                                <div className="space-y-1">
                                  {intent.examples.map((ex, idx) => (
                                    <p key={idx} className="text-xs text-zinc-400 italic">"{ex}"</p>
                                  ))}
                                </div>
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                </ScrollArea>
              </CardContent>
            </Card>
          </TabsContent>

          {/* TAB: Scripts */}
          <TabsContent value="scripts" className="space-y-6">
            <Card className="bg-zinc-900 border-zinc-800">
              <CardHeader>
                <CardTitle className="text-lg flex items-center gap-2">
                  <FileText className="w-5 h-5 text-blue-400" />
                  Scripts & Textes TTS
                </CardTitle>
                <CardDescription>
                  Modifiez les textes prononcés par Audrey pour chaque situation
                </CardDescription>
              </CardHeader>
              <CardContent>
                <ScrollArea className="h-[600px] pr-4">
                  <Accordion type="multiple" className="space-y-2">
                    {config?.scripts?.map((script) => (
                      <AccordionItem 
                        key={script.intent_id} 
                        value={script.intent_id} 
                        className="border border-zinc-800 rounded-lg px-4 bg-zinc-800/30"
                      >
                        <AccordionTrigger className="hover:no-underline">
                          <div className="flex items-center gap-3">
                            <Badge variant="outline" className="border-blue-500/30 text-blue-400">
                              {script.intent_id}
                            </Badge>
                            <span className="text-white">{script.intent_name || script.intent_id}</span>
                          </div>
                        </AccordionTrigger>
                        <AccordionContent className="pt-4 space-y-4">
                          {/* Main Prompts */}
                          <div className="space-y-3">
                            <Label className="text-xs text-zinc-500">Textes principaux (prononcés dans l'ordre)</Label>
                            {script.prompts?.map((prompt, idx) => (
                              <div key={idx} className="relative">
                                <Textarea
                                  value={prompt}
                                  onChange={(e) => {
                                    const newPrompts = [...script.prompts];
                                    newPrompts[idx] = e.target.value;
                                    updateScript(script.intent_id, 'prompts', newPrompts);
                                  }}
                                  className="bg-zinc-900 border-zinc-700 pr-10"
                                  rows={2}
                                />
                                <Badge className="absolute top-2 right-2 text-xs bg-zinc-700">
                                  {idx + 1}
                                </Badge>
                              </div>
                            ))}
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => {
                                const newPrompts = [...(script.prompts || []), ''];
                                updateScript(script.intent_id, 'prompts', newPrompts);
                              }}
                              className="w-full border-dashed"
                            >
                              <Plus className="w-4 h-4 mr-2" />
                              Ajouter un texte
                            </Button>
                          </div>

                          {/* Variations */}
                          {script.variations?.length > 0 && (
                            <div className="space-y-3">
                              <Label className="text-xs text-zinc-500">Variations (alternatives aléatoires)</Label>
                              {script.variations.map((variation, idx) => (
                                <Textarea
                                  key={idx}
                                  value={variation}
                                  onChange={(e) => {
                                    const newVariations = [...script.variations];
                                    newVariations[idx] = e.target.value;
                                    updateScript(script.intent_id, 'variations', newVariations);
                                  }}
                                  className="bg-zinc-900 border-zinc-700"
                                  rows={2}
                                />
                              ))}
                            </div>
                          )}

                          {/* Conditions */}
                          {script.conditions && (
                            <div className="p-3 bg-zinc-900 rounded-lg">
                              <Label className="text-xs text-zinc-500 mb-2 block">Conditions</Label>
                              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 text-xs">
                                {Object.entries(script.conditions).map(([key, value]) => (
                                  <div key={key} className="flex justify-between">
                                    <span className="text-zinc-500">{key}:</span>
                                    <span className="text-white">{String(value)}</span>
                                  </div>
                                ))}
                              </div>
                            </div>
                          )}
                        </AccordionContent>
                      </AccordionItem>
                    ))}
                  </Accordion>
                </ScrollArea>
              </CardContent>
            </Card>
          </TabsContent>

          {/* TAB: Rules */}
          <TabsContent value="rules" className="space-y-6">
            {/* Escalation Rules */}
            <Card className="bg-zinc-900 border-zinc-800">
              <CardHeader>
                <CardTitle className="text-lg flex items-center gap-2">
                  <Shield className="w-5 h-5 text-orange-400" />
                  Règles d'Escalade
                </CardTitle>
                <CardDescription>
                  Définissez quand et comment transférer vers un humain
                </CardDescription>
              </CardHeader>
              <CardContent>
                <ScrollArea className="h-[400px] pr-4">
                  <div className="space-y-3">
                    {config?.escalation_rules?.sort((a, b) => (b.priority || 0) - (a.priority || 0)).map((rule) => (
                      <div 
                        key={rule.id}
                        className={`p-4 rounded-lg border ${
                          rule.enabled ? 'border-orange-500/30 bg-zinc-800/50' : 'border-zinc-700 bg-zinc-800/30'
                        }`}
                      >
                        <div className="flex items-center justify-between mb-2">
                          <div className="flex items-center gap-3">
                            <Badge className="bg-orange-500/20 text-orange-400">
                              Prio {rule.priority || 0}
                            </Badge>
                            <p className="font-medium text-white">{rule.name}</p>
                          </div>
                          <Switch
                            checked={rule.enabled}
                            onCheckedChange={(checked) => updateEscalationRule(rule.id, 'enabled', checked)}
                          />
                        </div>
                        <p className="text-sm text-zinc-400 mb-2">{rule.description}</p>
                        <div className="flex flex-wrap gap-1">
                          {rule.trigger_conditions?.map((condition, idx) => (
                            <Badge key={idx} variant="outline" className="text-xs border-zinc-600">
                              {condition}
                            </Badge>
                          ))}
                        </div>
                        <div className="mt-2 text-xs text-zinc-500">
                          Action: <span className="text-orange-400">{rule.action}</span>
                        </div>
                      </div>
                    ))}
                  </div>
                </ScrollArea>
              </CardContent>
            </Card>

            {/* Stop Rules */}
            <Card className="bg-zinc-900 border-zinc-800">
              <CardHeader>
                <CardTitle className="text-lg flex items-center gap-2">
                  <AlertCircle className="w-5 h-5 text-red-400" />
                  Règles d'Arrêt
                </CardTitle>
                <CardDescription>
                  Conditions de fin d'appel automatique
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  {config?.stop_rules?.map((rule) => (
                    <div 
                      key={rule.id}
                      className={`p-4 rounded-lg border ${
                        rule.enabled ? 'border-red-500/30 bg-zinc-800/50' : 'border-zinc-700 bg-zinc-800/30'
                      }`}
                    >
                      <div className="flex items-center justify-between mb-2">
                        <p className="font-medium text-white">{rule.name}</p>
                        <Switch
                          checked={rule.enabled}
                          onCheckedChange={(checked) => {
                            setConfig(prev => {
                              const newConfig = JSON.parse(JSON.stringify(prev));
                              const ruleIdx = newConfig.stop_rules.findIndex(r => r.id === rule.id);
                              if (ruleIdx !== -1) {
                                newConfig.stop_rules[ruleIdx].enabled = checked;
                              }
                              return newConfig;
                            });
                          }}
                        />
                      </div>
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 text-sm">
                        <div>
                          <span className="text-zinc-500">Condition:</span>
                          <Badge variant="outline" className="ml-2 border-zinc-600">{rule.condition}</Badge>
                        </div>
                        <div>
                          <span className="text-zinc-500">Action:</span>
                          <span className="text-red-400 ml-2">{rule.action}</span>
                        </div>
                      </div>
                      {rule.goodbye_message && (
                        <div className="mt-2">
                          <span className="text-xs text-zinc-500">Message de fin:</span>
                          <p className="text-sm text-zinc-300 italic mt-1">"{rule.goodbye_message}"</p>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* TAB: Test */}
          <TabsContent value="test" className="space-y-6">
            <Card className="bg-zinc-900 border-zinc-800">
              <CardHeader>
                <CardTitle className="text-lg flex items-center gap-2">
                  <Sparkles className="w-5 h-5 text-purple-400" />
                  Test de Classification
                </CardTitle>
                <CardDescription>
                  Testez comment Audrey classifierait une phrase en temps réel
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  <div className="flex gap-3">
                    <Input
                      placeholder="Ex: Je veux savoir où en est mon dossier..."
                      value={testInput}
                      onChange={(e) => setTestInput(e.target.value)}
                      className="bg-zinc-800 border-zinc-700 flex-1"
                      onKeyPress={(e) => e.key === 'Enter' && testClassification()}
                    />
                    <Button onClick={testClassification} className="bg-purple-600 hover:bg-purple-700">
                      <Play className="w-4 h-4 mr-2" />
                      Tester
                    </Button>
                  </div>
                  
                  {testResult && (
                    <div className="p-4 bg-zinc-800 rounded-lg">
                      <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4 mb-4">
                        <div>
                          <p className="text-xs text-zinc-500">Intent détecté</p>
                          <p className="font-medium text-white text-lg">{testResult.intent || 'Aucun'}</p>
                        </div>
                        <div>
                          <p className="text-xs text-zinc-500">Confiance</p>
                          <p className="font-medium text-white text-lg">{Math.round((testResult.confidence || 0) * 100)}%</p>
                        </div>
                        <div>
                          <p className="text-xs text-zinc-500">Catégorie</p>
                          <Badge className="mt-1">{testResult.category || 'N/A'}</Badge>
                        </div>
                      </div>
                      
                      {testResult.triggers_matched?.length > 0 && (
                        <div className="pt-3 border-t border-zinc-700">
                          <p className="text-xs text-zinc-500 mb-2">Mots-clés détectés</p>
                          <div className="flex flex-wrap gap-1">
                            {testResult.triggers_matched.map((t, i) => (
                              <Badge key={i} variant="outline" className="border-green-500/30 text-green-400">{t}</Badge>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  )}

                  {/* Quick test examples */}
                  <div className="pt-4 border-t border-zinc-700">
                    <p className="text-xs text-zinc-500 mb-3">Exemples rapides (cliquez pour tester) :</p>
                    <div className="flex flex-wrap gap-2">
                      {[
                        "C'est urgent, j'ai besoin d'aide",
                        "Je voudrais savoir où en est mon dossier",
                        "J'appelle pour ma mère qui a déposé un dossier",
                        "Bonjour, j'appelle de la mairie",
                        "Je suis installateur partenaire TEF",
                        "Je voudrais devenir partenaire",
                        "J'ai une question sur vos services"
                      ].map((example, idx) => (
                        <Button
                          key={idx}
                          variant="outline"
                          size="sm"
                          className="text-xs border-zinc-700 hover:border-purple-500"
                          onClick={() => {
                            setTestInput(example);
                            setTimeout(() => testClassification(), 100);
                          }}
                        >
                          {example.substring(0, 30)}...
                        </Button>
                      ))}
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>

        {/* Save Confirmation Dialog */}
        <Dialog open={showSaveConfirm} onOpenChange={setShowSaveConfirm}>
          <DialogContent className="bg-zinc-900 border-zinc-800">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Save className="w-5 h-5 text-green-400" />
                Confirmer la sauvegarde
              </DialogTitle>
              <DialogDescription>
                Les modifications seront appliquées immédiatement à l'agent vocal Audrey.
                Cette action est irréversible.
              </DialogDescription>
            </DialogHeader>
            
            <div className="p-4 bg-zinc-800 rounded-lg">
              <p className="text-sm text-zinc-400 mb-2">Résumé des modifications :</p>
              <ul className="text-sm text-zinc-300 space-y-1">
                <li>• Configuration Audrey mise à jour</li>
                <li>• Le runtime sera automatiquement rechargé</li>
                <li>• Les appels en cours ne seront pas affectés</li>
              </ul>
            </div>
            
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowSaveConfirm(false)}>
                Annuler
              </Button>
              <Button 
                onClick={saveConfig}
                className="bg-green-600 hover:bg-green-700"
                disabled={saving}
              >
                {saving ? (
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                ) : (
                  <Save className="w-4 h-4 mr-2" />
                )}
                Confirmer et sauvegarder
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </TooltipProvider>
  );
}
